import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Button, Col, Container, Form, Row } from "react-bootstrap";
import { Formik } from "formik";
import * as Yup from "yup";

import SntRadio from "@cleanup/components/SntRadio/SntRadio";
import useOuterClick from "@cleanup/hooks/useOutsideElement";
import usePositionFilter from "@cleanup/hooks/usePositionFilter";
import ExcludeLabel from "@cleanup/components/AdvancedFilters/ExcludeLabel";
import GeneralFooterFilter from "@cleanup/components/GeneralFooterFilter/GeneralFooterFilter";
import {
  AdvanceFilter,
  FilterBody,
  FilterHeaderStyle,
} from "@cleanup/components/AdvancedFilters/FilterStyle";
import SntCloseSmallIcon from "@icons/SntCloseSmallIcon";
import SntArrowDownIcon from "@icons/SntArrowDownIcon";
import StringUtils from "@/utils/StringUtils";
import { LanguageKey } from "@/types/generated/LanguageKey";
import { translate } from "@/hooks/useLanguage";

interface Descriptor {
  _idx: string;
  label?: string;
  description: string;
}

interface FilterValue {
  operator?: string;
  eq?: string;
  lt?: string;
  gt?: string;
}

interface FilterData {
  filterValue?: FilterValue;
  notFilter?: boolean;
  idx?: string;
}

interface VersionFilterProps {
  descriptor: Descriptor;
  data?: FilterData;
  onChange?: (data: FilterData | { idx: string; data: null }) => void;
  disabled?: boolean;
}

interface InitialValues {
  smallerVal: string;
  greaterVal: string;
  betweenGreaterVal: string;
  betweenSmallerVal: string;
  equalVal: string;
}

/**
 * A dotted version, written as far as you care to: {@code 4}, {@code 4.0} and
 * {@code 4.0.6} are all accepted, and the missing parts simply are not
 * compared. That is what makes "everything in 4.0" expressible as
 * {@code >= 4.0} and {@code < 4.1} without anybody naming the last patch of
 * 4.0.
 */
const VERSION_PATTERN = /^\d+(\.\d+)*$/;

/** Which field each mode owns, so only its errors can block Apply. */
const FIELDS_BY_OPERATOR: Record<string, (keyof InitialValues)[]> = {
  EQ: ["equalVal"],
  GT: ["greaterVal"],
  LT: ["smallerVal"],
  BETWEEN: ["betweenSmallerVal", "betweenGreaterVal"],
};

const EMPTY_VALUES: InitialValues = {
  smallerVal: "",
  greaterVal: "",
  betweenGreaterVal: "",
  betweenSmallerVal: "",
  equalVal: "",
};

/**
 * Filters a version column by comparing versions rather than text.
 * <p>
 * Deliberately not a mode of {@code NumericFilter}, although the operators
 * look the same. A version is not a number: it has no unit and no duration to
 * convert, there is no histogram bucket to drill into, a spinner makes no sense
 * on it, and {@code 4.0} is a prefix of a range rather than a quantity. Sharing
 * the widget would have meant carrying all of that for a value it does not
 * apply to, and changing a component every numeric filter in the application
 * depends on.
 * <p>
 * What it does share is the popup shell every filter here uses — the trigger
 * button, the outside-click close, the positioning and the footer — so it
 * behaves like its neighbours. The comparison itself is done in the database,
 * by the VERSION filter type.
 */
const VersionFilter: React.FC<VersionFilterProps> = ({
  descriptor,
  data = {},
  onChange,
  disabled = false,
}) => {
  const [isShow, setShow] = useState(false);
  const [title, setTitle] = useState("");
  const [errors, setErrors] = useState<Record<string, unknown> | null>(null);
  const [selectedValue, setSelectedValue] = useState("");
  const [notFilter, setNotFilter] = useState(false);
  const [initialValues, setInitialValues] = useState<InitialValues>(EMPTY_VALUES);

  const popupStyleRef = useRef<Record<string, unknown>>({});
  const { popupRef, buttonRef, getPosition } = usePositionFilter();

  /**
   * Only the selected mode's fields are validated.
   * <p>
   * Formik validates the whole schema before it will call onSubmit, so marking
   * every field required would mean Apply never fires: the three modes you are
   * not using are empty by design. NumericFilter gets away with one fixed
   * schema because its fields default to 0 and 1; a version has no such
   * default, so the schema follows the selection instead.
   */
  const validationSchema = useMemo(() => {
    const owned = FIELDS_BY_OPERATOR[selectedValue] ?? [];
    const shape: Record<string, Yup.StringSchema> = {};
    (Object.keys(EMPTY_VALUES) as (keyof InitialValues)[]).forEach((field) => {
      const pattern = Yup.string().matches(VERSION_PATTERN, {
        message: translate(LanguageKey.version_filter_invalid_key),
        excludeEmptyString: true,
      });
      shape[field] = owned.includes(field)
        ? pattern.required(translate(LanguageKey.valid_required_key))
        : pattern;
    });
    return Yup.object(shape);
  }, [selectedValue]);

  useEffect(() => {
    const filterValue: FilterValue = data?.filterValue || {};
    const operator = filterValue.operator || "";
    let next = translate(LanguageKey.all_key);

    if (operator === "EQ") {
      next = `= ${filterValue.eq}`;
      setInitialValues({ ...EMPTY_VALUES, equalVal: String(filterValue.eq ?? "") });
    } else if (operator === "LT") {
      next = `< ${filterValue.lt}`;
      setInitialValues({ ...EMPTY_VALUES, smallerVal: String(filterValue.lt ?? "") });
    } else if (operator === "GT") {
      next = `>= ${filterValue.gt}`;
      setInitialValues({ ...EMPTY_VALUES, greaterVal: String(filterValue.gt ?? "") });
    } else if (operator === "BETWEEN") {
      next = StringUtils.replaceStr(
        translate(LanguageKey.between_and_key),
        String(filterValue.gt),
        String(filterValue.lt)
      );
      setInitialValues({
        ...EMPTY_VALUES,
        betweenSmallerVal: String(filterValue.gt ?? ""),
        betweenGreaterVal: String(filterValue.lt ?? ""),
      });
    }

    setSelectedValue(operator);
    setNotFilter(Boolean(data?.notFilter));
    setTitle(next);
  }, [data, data.filterValue, descriptor]);

  const getData = useCallback(() => {
    const newData = JSON.parse(JSON.stringify(data)) as FilterData;
    if (selectedValue === "EQ") {
      newData.filterValue = { operator: "EQ", eq: initialValues.equalVal };
    } else if (selectedValue === "LT") {
      newData.filterValue = { operator: "LT", lt: initialValues.smallerVal };
    } else if (selectedValue === "GT") {
      newData.filterValue = { operator: "GT", gt: initialValues.greaterVal };
    } else if (selectedValue === "BETWEEN") {
      // lower bound inclusive, upper exclusive — the same half-open interval
      // the VERSION filter applies in the database
      newData.filterValue = {
        operator: "BETWEEN",
        gt: initialValues.betweenSmallerVal,
        lt: initialValues.betweenGreaterVal,
      };
    } else {
      newData.filterValue = {};
    }
    newData.notFilter = notFilter;
    return newData;
  }, [data, selectedValue, initialValues, notFilter]);

  const isChanged = useCallback(() => {
    const previous = data.filterValue || {};
    const next = getData();
    const nextValue = next.filterValue || {};
    return !(
      previous.eq === nextValue.eq &&
      previous.gt === nextValue.gt &&
      previous.lt === nextValue.lt &&
      previous.operator === nextValue.operator &&
      (data.notFilter === next.notFilter ||
        (data.notFilter === undefined && next.notFilter === false) ||
        (data.notFilter === false && next.notFilter === undefined))
    );
  }, [data.filterValue, data.notFilter, getData]);

  const applyAndClose = useCallback(() => {
    setShow(false);
    if (isChanged() && onChange) {
      onChange(getData());
    }
  }, [isChanged, onChange, getData]);

  const refWrapper = useOuterClick(() => {
    if (isShow && errors && Object.keys(errors).length === 0) {
      applyAndClose();
    }
  });

  const onClickButton = () => {
    if (isShow) {
      applyAndClose();
    } else {
      popupStyleRef.current = getPosition();
      setShow(true);
    }
  };

  const onClearFilter = () => {
    setSelectedValue("");
    setNotFilter(false);
    setShow(false);
    onChange?.({ idx: descriptor._idx, data: null });
  };

  const excludeLabel = useMemo(
    () => (data.notFilter ? <ExcludeLabel /> : null),
    [data.notFilter]
  );

  /** One mode: its radio, and the field(s) it enables. */
  const mode = (
    operator: string,
    label: string,
    fields: { name: keyof InitialValues; value: string }[],
    handleChange: React.ChangeEventHandler,
    fieldErrors: Record<string, unknown>
  ) => (
    <>
      <div className="mb-3 mt-3">
        <SntRadio
          value={operator}
          selectedValue={selectedValue}
          onChange={(value) => value !== undefined && setSelectedValue(value)}
          label={label}
        />
      </div>
      <Row className="mb-3">
        {fields.map((field) => (
          <Col key={field.name}>
            <Form.Control
              name={field.name}
              value={field.value}
              onChange={(e) => {
                // Formik holds the typed value, but getData() reads
                // initialValues — both have to move or Apply sees no change
                handleChange(e);
                const typed = (e.target as HTMLInputElement).value;
                setInitialValues((previous) => ({ ...previous, [field.name]: typed }));
              }}
              disabled={selectedValue !== operator}
              placeholder="4.0.6"
              isInvalid={selectedValue === operator && !!fieldErrors[field.name]}
            />
            {selectedValue === operator && !!fieldErrors[field.name] && (
              <Form.Control.Feedback type="invalid" className="d-block">
                {String(fieldErrors[field.name])}
              </Form.Control.Feedback>
            )}
          </Col>
        ))}
      </Row>
    </>
  );

  return (
    <AdvanceFilter ref={refWrapper}>
      <Button
        ref={buttonRef}
        variant="sensolus-greylight"
        title={descriptor.description}
        disabled={disabled}
        onClick={onClickButton}
      >
        {selectedValue && (
          <SntCloseSmallIcon
            className="me-1"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              onClearFilter();
            }}
          />
        )}
        <span>{descriptor.label || descriptor.description}</span>:{" "}
        <span>{excludeLabel}</span>
        <FilterHeaderStyle>{title}</FilterHeaderStyle>
        <SntArrowDownIcon />
      </Button>
      <FilterBody
        ref={popupRef}
        style={{ display: isShow ? "block" : "none", ...popupStyleRef.current }}
      >
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          enableReinitialize={true}
          onSubmit={() => applyAndClose()}
        >
          {({ handleSubmit, handleChange, values, errors: formErrors }) => {
            // the schema only covers the selected mode, so anything here is
            // about a field the user is actually filling in
            setTimeout(() => setErrors(formErrors as Record<string, unknown>), 0);
            return (
              <form onSubmit={handleSubmit}>
                <Container>
                  {mode(
                    "EQ",
                    translate(LanguageKey.has_equal_value_key),
                    [{ name: "equalVal", value: values.equalVal }],
                    handleChange,
                    formErrors as Record<string, unknown>
                  )}
                  {mode(
                    "GT",
                    translate(LanguageKey.version_filter_at_least_key),
                    [{ name: "greaterVal", value: values.greaterVal }],
                    handleChange,
                    formErrors as Record<string, unknown>
                  )}
                  {mode(
                    "LT",
                    translate(LanguageKey.version_filter_below_key),
                    [{ name: "smallerVal", value: values.smallerVal }],
                    handleChange,
                    formErrors as Record<string, unknown>
                  )}
                  {mode(
                    "BETWEEN",
                    translate(LanguageKey.version_filter_range_key),
                    [
                      { name: "betweenSmallerVal", value: values.betweenSmallerVal },
                      { name: "betweenGreaterVal", value: values.betweenGreaterVal },
                    ],
                    handleChange,
                    formErrors as Record<string, unknown>
                  )}
                </Container>
                <GeneralFooterFilter
                  onClearFilter={() => onClearFilter()}
                  onAppliedFilter={() => {
                    if (Object.keys(formErrors).length === 0) {
                      handleSubmit();
                    }
                  }}
                  isNotFilter={notFilter}
                  onCheckNotFilter={(value) => setNotFilter(value)}
                />
              </form>
            );
          }}
        </Formik>
      </FilterBody>
    </AdvanceFilter>
  );
};

export default VersionFilter;
